import { 
  collection, 
  doc, 
  getDocs, 
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  ProjectAssignment, 
  OptimizedAssignment,
  OptimizedSubTask,
  OptimizedRoutineAssignmentDate,
  StudentStatusInfo,
  MigrationProgress,
  MigrationResult,
  MigrationOptions
} from '../types';

/**
 * 데이터 마이그레이션 서비스
 * 기존 서브컬렉션 구조를 배열 기반 구조로 변환
 */
export class DataMigrationService {
  private progress: MigrationProgress;
  private result: MigrationResult;

  constructor() {
    this.progress = {
      total: 0,
      completed: 0,
      current: '',
      errors: [],
      startTime: new Date()
    };
    
    this.result = {
      success: false,
      totalProcessed: 0,
      successCount: 0,
      errorCount: 0,
      errors: [],
      duration: 0
    };
  }

  /**
   * 전체 마이그레이션 실행
   */
  async migrateAllData(
    userId: string, 
    classId: string, 
    options: MigrationOptions = {}
  ): Promise<MigrationResult> {
    const startTime = Date.now();
    
    try {
      console.log('🚀 데이터 마이그레이션 시작...');
      
      // 1. 일반 과제 마이그레이션
      await this.migrateAssignments(userId, classId, options);
      
      // 2. 프로젝트 과제 마이그레이션
      await this.migrateProjectAssignments(userId, classId, options);
      
      // 3. 루틴 과제 마이그레이션
      await this.migrateRoutineAssignments(userId, classId, options);
      
      this.result.success = true;
      this.result.duration = Date.now() - startTime;
      
      console.log('✅ 데이터 마이그레이션 완료:', this.result);
      return this.result;
      
    } catch (error) {
      console.error('❌ 데이터 마이그레이션 실패:', error);
      this.result.success = false;
      this.result.duration = Date.now() - startTime;
      return this.result;
    }
  }

  /**
   * 일반 과제 마이그레이션
   */
  private async migrateAssignments(
    userId: string, 
    classId: string, 
    options: MigrationOptions
  ): Promise<void> {
    console.log('📝 일반 과제 마이그레이션 시작...');
    
    const assignmentsRef = collection(db, 'users', userId, 'classes', classId, 'generalAssignments');
    const assignmentsSnap = await getDocs(assignmentsRef);
    
    this.progress.total += assignmentsSnap.docs.length;
    
    const batch = writeBatch(db);
    let batchCount = 0;
    
    for (const assignmentDoc of assignmentsSnap.docs) {
      try {
        this.progress.current = `일반 과제: ${assignmentDoc.id}`;
        
        
        // 서브컬렉션에서 멤버 상태들 읽기
        const studentStatuses = await this.loadStudentStatusesFromSubCollection(
          userId, 
          classId, 
          'assignmentStatuses', 
          assignmentDoc.id
        );
        
        if (!options.dryRun) {
          // 새로운 구조로 업데이트
          const optimizedAssignment: Partial<OptimizedAssignment> = {
            studentStatuses: studentStatuses
          };
          
          batch.update(assignmentDoc.ref, optimizedAssignment);
          batchCount++;
          
          // 배치 크기 제한 (Firestore 제한: 500개)
          if (batchCount >= (options.batchSize || 100)) {
            await batch.commit();
            batchCount = 0;
          }
        }
        
        this.progress.completed++;
        this.result.successCount++;
        
        console.log(`✅ 일반 과제 ${assignmentDoc.id} 마이그레이션 완료`);
        
      } catch (error) {
        console.error(`❌ 일반 과제 ${assignmentDoc.id} 마이그레이션 실패:`, error);
        this.progress.errors.push(`일반 과제 ${assignmentDoc.id}: ${error}`);
        this.result.errors.push({
          type: 'assignment',
          id: assignmentDoc.id,
          error: String(error)
        });
        this.result.errorCount++;
      }
    }
    
    // 남은 배치 커밋
    if (batchCount > 0) {
      await batch.commit();
    }
    
    console.log('✅ 일반 과제 마이그레이션 완료');
  }

  /**
   * 프로젝트 과제 마이그레이션
   */
  private async migrateProjectAssignments(
    userId: string, 
    classId: string, 
    options: MigrationOptions
  ): Promise<void> {
    console.log('📝 프로젝트 과제 마이그레이션 시작...');
    
    const projectAssignmentsRef = collection(db, 'users', userId, 'classes', classId, 'projectAssignments');
    const projectAssignmentsSnap = await getDocs(projectAssignmentsRef);
    
    this.progress.total += projectAssignmentsSnap.docs.length;
    
    const batch = writeBatch(db);
    let batchCount = 0;
    
    for (const projectDoc of projectAssignmentsSnap.docs) {
      try {
        this.progress.current = `프로젝트 과제: ${projectDoc.id}`;
        
        const projectAssignment = projectDoc.data() as ProjectAssignment;
        
        // 각 하위 과제별로 마이그레이션
        for (const subTask of projectAssignment.subTasks) {
          const subTaskStatuses = await this.loadStudentStatusesFromSubCollection(
            userId, 
            classId, 
            'projectAssignmentStatuses', 
            projectDoc.id,
            subTask.id
          );
          
          if (!options.dryRun) {
            // 하위 과제 문서 업데이트
            const subTaskRef = doc(
              db, 
              'users', 
              userId, 
              'classes', 
              classId, 
              'projectAssignments', 
              projectDoc.id, 
              'subTasks', 
              subTask.id
            );
            
            const optimizedSubTask: Partial<OptimizedSubTask> = {
              studentStatuses: subTaskStatuses
            };
            
            batch.update(subTaskRef, optimizedSubTask);
            batchCount++;
            
            // 배치 크기 제한
            if (batchCount >= (options.batchSize || 100)) {
              await batch.commit();
              batchCount = 0;
            }
          }
        }
        
        this.progress.completed++;
        this.result.successCount++;
        
        console.log(`✅ 프로젝트 과제 ${projectDoc.id} 마이그레이션 완료`);
        
      } catch (error) {
        console.error(`❌ 프로젝트 과제 ${projectDoc.id} 마이그레이션 실패:`, error);
        this.progress.errors.push(`프로젝트 과제 ${projectDoc.id}: ${error}`);
        this.result.errors.push({
          type: 'project',
          id: projectDoc.id,
          error: String(error)
        });
        this.result.errorCount++;
      }
    }
    
    // 남은 배치 커밋
    if (batchCount > 0) {
      await batch.commit();
    }
    
    console.log('✅ 프로젝트 과제 마이그레이션 완료');
  }

  /**
   * 루틴 과제 마이그레이션
   */
  private async migrateRoutineAssignments(
    userId: string, 
    classId: string, 
    options: MigrationOptions
  ): Promise<void> {
    console.log('📝 루틴 과제 마이그레이션 시작...');
    
    const routineAssignmentsRef = collection(db, 'users', userId, 'classes', classId, 'routineAssignmentStatuses');
    const routineAssignmentsSnap = await getDocs(routineAssignmentsRef);
    
    this.progress.total += routineAssignmentsSnap.docs.length;
    
    const batch = writeBatch(db);
    let batchCount = 0;
    
    for (const routineDoc of routineAssignmentsSnap.docs) {
      try {
        this.progress.current = `루틴 과제: ${routineDoc.id}`;
        
        // 날짜별 서브컬렉션 확인
        const datesRef = collection(routineDoc.ref, 'dates');
        const datesSnap = await getDocs(datesRef);
        
        for (const dateDoc of datesSnap.docs) {
          const studentStatuses = await this.loadStudentStatusesFromSubCollection(
            userId, 
            classId, 
            'routineAssignmentStatuses', 
            routineDoc.id,
            'dates',
            dateDoc.id
          );
          
          if (!options.dryRun) {
            // 날짜별 문서 업데이트
            const optimizedDate: Partial<OptimizedRoutineAssignmentDate> = {
              studentStatuses: studentStatuses
            };
            
            batch.update(dateDoc.ref, optimizedDate);
            batchCount++;
            
            // 배치 크기 제한
            if (batchCount >= (options.batchSize || 100)) {
              await batch.commit();
              batchCount = 0;
            }
          }
        }
        
        this.progress.completed++;
        this.result.successCount++;
        
        console.log(`✅ 루틴 과제 ${routineDoc.id} 마이그레이션 완료`);
        
      } catch (error) {
        console.error(`❌ 루틴 과제 ${routineDoc.id} 마이그레이션 실패:`, error);
        this.progress.errors.push(`루틴 과제 ${routineDoc.id}: ${error}`);
        this.result.errors.push({
          type: 'routine',
          id: routineDoc.id,
          error: String(error)
        });
        this.result.errorCount++;
      }
    }
    
    // 남은 배치 커밋
    if (batchCount > 0) {
      await batch.commit();
    }
    
    console.log('✅ 루틴 과제 마이그레이션 완료');
  }

  /**
   * 서브컬렉션에서 멤버 상태들 읽기
   */
  private async loadStudentStatusesFromSubCollection(
    userId: string,
    classId: string,
    collectionName: string,
    assignmentId: string,
    ...subPaths: string[]
  ): Promise<StudentStatusInfo[]> {
    const studentsRef = collection(db, 'users', userId, 'classes', classId, collectionName, assignmentId, ...subPaths, 'students');
    const studentsSnap = await getDocs(studentsRef);
    
    return studentsSnap.docs.map(doc => {
      const data = doc.data();
      return {
        studentId: doc.id,
        status: data.status || 'pending',
        earnedPoints: data.earnedPoints || 0,
        memo: data.memo || '',
        notes: data.notes || '',
        updatedAt: data.updatedAt?.toDate() || new Date()
      };
    });
  }

  /**
   * 마이그레이션 진행 상태 조회
   */
  getProgress(): MigrationProgress {
    return { ...this.progress };
  }

  /**
   * 마이그레이션 결과 조회
   */
  getResult(): MigrationResult {
    return { ...this.result };
  }
}

// 싱글톤 인스턴스
export const dataMigrationService = new DataMigrationService();
